import React, { ReactElement, useEffect, useState } from 'react';
import { Box, CircularProgress, Stack, SvgIcon } from '@mui/material';
import PolicyCard from './components/PolicyContentCard';
import PolicyInformationCard from './components/PolicyInformationCard';
import PolicyModeCard from './components/PolicyModeCard';
import PolicyReviewCard from './components/PolicyReviewCard';
import {
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
} from 'shared/components/AppComponents';
import { ReactComponent as Send } from 'assets/images/ic_send.svg';
import { ReactComponent as Save } from 'assets/images/ic_save.svg';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik, FormikHelpers } from 'formik';
import { PolicyManagementScheme } from 'validators/PolicyManagementScheme';
import PageHeader from 'shared/components/PageHeader';
import { PoliciesService } from 'services';
import { Toast, Utils } from 'shared/helpers';
import ScrollToError from 'shared/components/ScrollToError';

const AddPolicyManagement = (): ReactElement => {
  const navigate = useNavigate();
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const { policyId } = useParams();
  const [policyDetail, setPolicyDetail] = useState({
    title: '',
    version: '1.0.0',
    owner: '',
    effectiveDate: '',
    status: 'Draft',
    acknowledgementRequired: false,
    description: '',
    reviewIntervalMonths: 0,
    reviewDate: '',
    policyMode: 'Content',
    content: '',
    policyUrl: '',
    notes: '',
  });
  const [isLoadingSubmit, setLoadingSubmit] = useState(false);
  const [isLoadingDraft, setLoadingDraft] = useState(false);

  // API Calls
  const loadDetails = (): void => {
    setIsLoadingDetail(true);
    PoliciesService.detail(`${policyId}`)
      .then((response) => {
        const { data } = response.data;
        setPolicyDetail(data);
      })
      .catch(() => {
        navigate('/policy-management');
      })
      .finally(() => {
        setIsLoadingDetail(false);
      });
  };
  const createRecord = (payload: any, callback: any): void => {
    PoliciesService.create(payload)
      .then((response) => {
        Toast.showSuccessMessage('Policy created successfully');
        callback(response);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
        callback(false);
      });
  };

  const updateRecord = (payload: any, callback: any): void => {
    PoliciesService.update(`${policyId}`, payload)
      .then(() => {
        Toast.showSuccessMessage('Policy updated successfully');
        navigate(-1);
      })
      .catch(() => {
        callback(false);
      });
  };

  // UI Actions
  const actionOnSubmit = (
    formValues: any,
    formik: FormikHelpers<any>
  ): void => {
    if (policyId) {
      const payload = { ...formValues };
      if (
        formValues.content !== policyDetail.content ||
        formValues.policyMode !== policyDetail.policyMode
      ) {
        payload.notes = 'content updated';
      } else if (formValues.title !== policyDetail.title) {
        payload.notes = 'title updated';
      }
      updateRecord(payload, (response: any) => {
        formik.setSubmitting(false);
        if (typeof response === 'object') {
          navigate(-1);
        }
      });
    } else {
      createRecord(formValues, (response: any) => {
        formik.setSubmitting(false);
        if (typeof response === 'object') {
          navigate(-1);
        }
      });
    }
  };

  // Hooks
  useEffect(() => {
    if (policyId) {
      loadDetails();
    }
  }, [policyId]);

  // UI Elements
  return (
    <>
      <PageHeader title={policyId ? 'Edit Policy' : 'Create New Policy'} />
      {isLoadingDetail && (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      )}
      {!isLoadingDetail && (
        <Formik
          initialValues={policyDetail}
          enableReinitialize
          validateOnMount
          validationSchema={PolicyManagementScheme}
          onSubmit={(formValues, formik) => {
            actionOnSubmit(formValues, formik);
          }}
        >
          {({ handleSubmit, setFieldValue, isSubmitting }) => (
            <Box className="policy-management add-policy-management-wrapper">
              <ScrollToError />
              <Box className="add-policy-content">
                <PolicyInformationCard />
                <PolicyReviewCard />
                <PolicyModeCard />
                <PolicyCard />
              </Box>
              <Stack
                direction="row-reverse"
                spacing={2}
                className="add-policy-group-button"
              >
                <AppPrimaryButton
                  title={policyId ? 'Update Policy' : 'Submit Policy'}
                  isLoading={isLoadingSubmit}
                  disabled={isSubmitting}
                  startIcon={
                    <SvgIcon
                      component={Send}
                      inheritViewBox
                      className="view-icon"
                      fontSize="inherit"
                      htmlColor="#3D3C42"
                    />
                  }
                  onClick={() => {
                    setLoadingSubmit(true);
                    handleSubmit();
                  }}
                />
                <AppPrimaryButton
                  title="Save Draft"
                  isLoading={isLoadingDraft}
                  disabled={isSubmitting}
                  startIcon={
                    <SvgIcon
                      component={Save}
                      inheritViewBox
                      className="view-icon"
                      fontSize="inherit"
                      htmlColor="#3D3C42"
                    />
                  }
                  onClick={() => {
                    setFieldValue('status', 'Draft', true);
                    setLoadingDraft(true);
                    handleSubmit();
                  }}
                />
                <AppPrimaryButtonOutlined
                  title="Cancel"
                  onClick={() => navigate(-1)}
                  disabled={isSubmitting}
                />
              </Stack>
            </Box>
          )}
        </Formik>
      )}
    </>
  );
};

export default AddPolicyManagement;
